import React, { useEffect, useRef, useState } from "react";
import FundSelect from "../FundSelect";
import Buttons from "../../atoms/Buttons";
import { Card, Col, Row } from "react-bootstrap";
import "./manageflexiaccess.scss";
import ExploreAndChangeInvestment from "../ExploreAndChangeInvestments";
import DoughnutChart from "../../atoms/DoughnutChart";
import {
  addCommas,
  allocateRemainingPercentage,
  calculateUnits,
  extractStrategies,
  updateCMSFunds,
} from "../../helpers/helpers";
import { Link, useNavigate } from "react-router-dom";
import { Icons } from "../../atoms/Icons";
import { ModalOverlay } from "../../atoms/Modal";
import { useDispatch, useSelector } from "react-redux";
import { bindActionCreators } from "redux";
import { actionCreators } from "../../redux/actions";

const ManageFlexiAccess = (props) => {
  const navigate = useNavigate();
  const [chooseFlexiIncome, setChooseFlexiIncome] = useState(false);
  const [flexiAccessCurrentIncome, setFlexiAccessCurrentIncome] = useState(0);
  const [flexiAccessDuration, setFlexiAccessDuration] = useState("Monthly");
  const [flexiAccessIncomeIllustration, setFlexiAccessIncomeIllustration] =
    useState(false);
  const [updatedFlexiObject, setUpdatedFlexiObject] = useState({});
  const [showFlexiSummaryPopup, setShowFlexiSummaryPopup] = useState(false);
  const [finalResult, setFinalResult] = useState();
  const [changesConfirmPopup, setChangesConfirmPopup] = useState(false);
  const [checkIsDirty, setCheckIsDirty] = useState(false);
  const popupShown = useRef(false);
  const errorShown = useRef(false);

  const { fundSwitchFetching, fundSwitchData, fundSwitchError } = useSelector(
    (state) => state.fundSwitch // eslint-disable-line
  );

  const dispatch = useDispatch();

  const { savingsFundSwitch } = bindActionCreators(actionCreators, dispatch);

  useEffect(() => {
    if (
      fundSwitchFetching === false &&
      fundSwitchData.every((obj) => obj.isSuccess) === true &&
      popupShown.current === true
    ) {
      setShowFlexiSummaryPopup(false);
      setChangesConfirmPopup(true);
      errorShown.current = false;
    } else if (popupShown.current === true) {
      errorShown.current = true;
    }
  }, [fundSwitchFetching, fundSwitchData, fundSwitchError]);

  const drawdownData = JSON.parse(localStorage.getItem("initialDrawdown"));

  // prettier-ignore
  const [initialStrategies] = useState(
    extractStrategies(drawdownData).map((strategy) => ({
      ...strategy,
      strategyPercentage: strategy.balancePercent ? strategy.balancePercent : 0,
      incomePercentage: (
        (strategy.currentIncome / strategy.currentValue) *
        100
      ).toFixed(2),
    })),
  );

  // prettier-ignore
  const [flexiCurrentValue] = useState(
    initialStrategies.find((strategy) => strategy.strategyName === "Flexi-access")
  );

  const [cmsFund, setCMSFund] = useState(
    props.CMSfunds?.filter(
      (fund) =>
        fund?.fundName?.toLowerCase() !== "legacy fund" &&
        fund?.fundName?.toLowerCase() !== "safety net fund" &&
        fund?.fundName?.toLowerCase() !== "future annuity income fund" &&
        fund?.fundName?.toLowerCase() !== "managed income fund",
    ),
  );
  // prettier-ignore
  const [initialFlexiFund] = useState(
    props.directFromManage === true
      ? assignFundPercentage(props.flexiAcessObject)?.investments
      : props.flexiAcessObject?.investments?.map((fund) => ({
          ...fund,
          fundPercentage: fund.balancePercent,
        }))
  );
  const [selectedFlexiFund, setSelectedFlexiFund] = useState(
    props.directFromManage === true
      ? assignFundPercentage(props.flexiAcessObject)?.investments
      : props.flexiAcessObject?.investments?.map((fund) => ({
          ...fund,
          fundPercentage: fund.balancePercent,
        })),
  );

  useEffect(() => {
    const isDifferent =
      initialFlexiFund.length !== selectedFlexiFund.length ||
      !initialFlexiFund.every((fund1) =>
        selectedFlexiFund.some(
          (fund2) =>
            fund1.investmentId === fund2.investmentId &&
            fund1.fundPercentage === parseFloat(fund2.fundPercentage),
        ),
      );
    setCheckIsDirty(isDifferent);
  }, [initialFlexiFund, selectedFlexiFund]);

  useEffect(() => {
    if (props.directFromManage === true) {
      setUpdatedFlexiObject(assignFundPercentage(props?.flexiAcessObject));
    } else {
      setUpdatedFlexiObject(props?.flexiAcessObject);
    }
    updateCMSFunds(selectedFlexiFund, cmsFund);
    setFlexiAccessCurrentIncome(props?.flexiAcessObject.newCurrentIncome);
    setFlexiAccessDuration(props?.flexiAcessObject.duration);
  }, []);

  function assignFundPercentage(strategy) {
    let currentValue = strategy.currentValue;
    let cashBalance = 0;

    // Remove the "cash" object and deduct its balance from currentValue
    strategy.investments = strategy.investments.filter((investment) => {
      if (investment.fundName?.toLowerCase() === "cash") {
        cashBalance = investment.balance;
        currentValue -= cashBalance;
        return false; // Remove the "cash" object
      }
      return true;
    });

    // Calculate the new percentages for the remaining investments
    strategy.investments.forEach((investment) => {
      let fundPercentage = (investment.balance / currentValue) * 100;
      investment.fundPercentage = Number.isInteger(fundPercentage)
        ? fundPercentage
        : parseFloat(fundPercentage.toFixed(2));
    });

    // Adjust the percentages to ensure they sum to 100%
    const totalPercentage = strategy.investments.reduce(
      (sum, investment) => sum + investment.fundPercentage,
      0,
    );
    const adjustmentFactor = 100 / totalPercentage;

    strategy.investments.forEach((investment) => {
      investment.fundPercentage = parseFloat(
        (investment.fundPercentage * adjustmentFactor).toFixed(2),
      );
    });

    return strategy;
  }

  const [allocateFunds, setAllocateFunds] = useState(
    props.directFromManage === true ? true : false,
  );

  const handleCheckboxChange = (cardId) => {
    setCMSFund((prevCardsData) => {
      return prevCardsData.map((card) => {
        if (card && card.investmentId === cardId) {
          return { ...card, checked: !card.checked };
        }
        return card;
      });
    });
  };

  // prettier-ignore
  const handleContinue = () => {
    const newFunds = [...selectedFlexiFund]; // Create a copy of the current funds array

    cmsFund.forEach((obj) => {
      if (obj?.checked) {
        if (!newFunds.some((item) => item.investmentId === obj?.investmentId)) {
          obj.balance = 0;
          obj.fundPercentage = 0;
          newFunds.push(obj);
        }
      } else {
        const index = newFunds.findIndex(
          (item) => item.investmentId === obj?.investmentId,
        );
        if (index !== -1) {
          newFunds.splice(index, 1); // Remove the object from the array
        }
      }
    });
    setSelectedFlexiFund(newFunds); // Update the funds state with the new array
    setAllocateFunds(true);
  }

  // prettier-ignore
  const handleOnFlexiAccessSliderChange = (
    cardId,
    percentageValue,
    currentValue
  ) => {
    setSelectedFlexiFund((prevFlexiFund) => {
      return prevFlexiFund.map((fund) => {
        if (fund.investmentId === cardId) {
          return {
            ...fund,
            fundPercentage: percentageValue,
            currentValue: currentValue,
            balance: currentValue,
          };
        }
        return fund;
      });
    });
  };

  const handleFinalContinue = () => {
    if (props.directFromManage === true) {
      const initialStrategiesMap = new Map(
        updatedFlexiObject.investments.map((obj) => [obj.investmentId, obj]),
      );
      const combinedArray = selectedFlexiFund.map((obj2) => {
        const matchingObj = initialStrategiesMap.get(obj2.investmentId);
        if (matchingObj) {
          return {
            investmentId: obj2.investmentId || obj2.fundId,
            fundName: obj2.fundName,
            balance1: matchingObj.balance,
            balance2: obj2.balance,
            fundPercentage1: matchingObj.fundPercentage,
            fundPercentage2: obj2.fundPercentage,
            sellUnitPrice: obj2.sellUnitPrice,
          };
        } else {
          return {
            investmentId: obj2.investmentId !== null ? obj2.investmentId : null,
            fundName: obj2.fundName,
            balance1:
              matchingObj?.balance !== undefined ? matchingObj.balance : 0,
            balance2: obj2.balance,
            fundPercentage1: 0,
            fundPercentage2: obj2.fundPercentage,
            sellUnitPrice: obj2.sellUnitPrice,
          };
        }
      });
      setFinalResult(combinedArray);
      setShowFlexiSummaryPopup(true);
    } else {
      setFlexiAccessCurrentIncome(props.flexiAcessObject.currentIncome);
      setFlexiAccessDuration(props.flexiAcessObject.duration);
      setChooseFlexiIncome(true);
    }
  };

  // prettier-ignore
  const chooseFlexiAcessHandleContinue = (flexiAccessIncome, period) => {
    setFlexiAccessCurrentIncome(flexiAccessIncome);
    setFlexiAccessDuration(period);
    setChooseFlexiIncome(false);
    setFlexiAccessIncomeIllustration(true);
  };

  const handleProceedWithoutIncome = (flexiAccessIncome, period) => {
    setFlexiAccessCurrentIncome(flexiAccessIncome);
    setFlexiAccessDuration(period);
    setChooseFlexiIncome(false);
    setFlexiAccessIncomeIllustration(true);
  };

  const handleIllustrationContinue = () => {
    props.strategies.forEach((strategy) => {
      if (strategy.strategyName === "Flexi-access") {
        strategy.newCurrentIncome = flexiAccessCurrentIncome;
        strategy.duration = flexiAccessDuration;
        strategy.investments = selectedFlexiFund;
      }
    });
    let directFromManage = props.directFromManage;
    let strategies = props.strategies;
    let personInfo = props.personInfo;
    let combinedState = {
      drawdownData,
      strategies,
      directFromManage,
      personInfo,
    };
    navigate("/manage/investments", { state: combinedState });
  };

  const handleSummaryContinue = () => {
    popupShown.current = true;
    handleCurrentSubmit();
  };

  const handleConfirm = (event) => {
    event.preventDefault();
    let toggle = "drawdown";
    let combinedState = {
      toggle,
    };
    navigate("/manage?tab=investments", { state: combinedState });
  };

  const handleAddAnotherFund = () => {
    setAllocateFunds(false);
  };

  const handleBack = (event) => {
    const back = true;
    event.preventDefault();
    if (allocateFunds === true && props.directFromManage === true) {
      let combinedState = {
        drawdownData,
        strategies: props.strategies,
        personInfo: props.personInfo,
        back,
      };
      navigate("/manage/investments", { state: combinedState });
    } else if (allocateFunds === false && props.directFromManage === false) {
      let combinedState = {
        drawdownData,
        strategies: props.strategies,
        personInfo: props.personInfo,
        back,
      };
      navigate("/manage/investments", { state: combinedState });
    } else if (allocateFunds === false && props.directFromManage === true) {
      setAllocateFunds(true);
    } else if (allocateFunds === true && props.directFromManage === false) {
      setAllocateFunds(false);
    }
  };

  const handleCancel = () => {
    let combinedState = { drawdownData, personInfo: props.personInfo };
    navigate("/manage/investments", { state: combinedState });
  };

  const handleCurrentSubmit = () => {
    let transformedData = {
      fromInvestments: [],
      toInvestments: [],
      strategyName: "currentInvestmentsOnly",
    };
    finalResult?.forEach((investment) => {
      if (
        investment &&
        investment.fundPercentage1 > investment.fundPercentage2
      ) {
        transformedData.fromInvestments.push({
          investmentId: investment.investmentId,
          unitAmount: calculateUnits(
            investment.balance1 - investment.balance2,
            investment.sellUnitPrice,
          ),
        });
      } else if (
        investment &&
        investment.fundPercentage1 < investment.fundPercentage2
      ) {
        transformedData.toInvestments.push({
          investmentId: investment.investmentId,
          percentageAmount:
            investment.fundPercentage2 - investment.fundPercentage1,
          amount: investment.balance2,
        });
      }
    });
    transformedData = allocateRemainingPercentage(transformedData);
    savingsFundSwitch(
      transformedData,
      props?.flexiAcessObject?.investorId,
      props?.flexiAcessObject?.membershipId // eslint-disable-line
    );
  };

  return (
    <div className="mf-parent">
      <Row className="mf-eac-back-button mb-2 mt-3">
        <Col lg={12} md={12} sm={12} xs={12}>
          <Link className="mf-eac-back-link" to="#" onClick={handleBack}>
            <Icons iconname="chevron-left" variant="icon-sixteen-px" />
            Back
          </Link>
        </Col>
      </Row>
      <Card className="mf-card mb-4">
        <div>
          {allocateFunds ? (
            <>
              <div className="d-none d-xl-block d-lg-block d-md-block">
                <Row>
                  <Col xl={3} lg={3} md={3} className="mf-doughnut-chart-small">
                    <div>
                      <DoughnutChart
                        percentages={selectedFlexiFund.map(
                          (fund) => fund.fundPercentage, // eslint-disable-line
                        )}
                        colorPallete={selectedFlexiFund.map(
                          (fund) => fund.color, // eslint-disable-line
                        )}
                        showValue={false}
                      />
                    </div>
                  </Col>
                  <Col xl={9} lg={9} md={9} className="mf-eac-pots-details">
                    <div className="mf-eac-pot-name">Flexi-access value</div>
                    <div className="mf-eac-pot-value mb-3">
                      £
                      {props.directFromManage
                        ? addCommas(
                            Math.trunc(props.flexiAcessObject.currentValue), // eslint-disable-line
                          )
                        : addCommas(
                            Math.trunc(props.flexiAcessObject.newCurrentValue), // eslint-disable-line
                          )}
                      .
                      <span className="mf-eac-pot-value-small-digit">
                        {props.directFromManage
                          ? (props.flexiAcessObject.currentValue % 1)
                              .toFixed(2)
                              .substring(2)
                          : (props.flexiAcessObject.newCurrentValue % 1)
                              .toFixed(2)
                              .substring(2)}
                      </span>
                    </div>
                    <div className="mf-eac-last-checked-date">
                      As of {props.flexiAcessObject.correctDate}
                    </div>
                  </Col>
                </Row>
                <hr />
                <div className="mf-fund-desc-parent">
                  <Row>
                    <Col xl={8} lg={8} md={8} className="mf-fund-desc">
                      These are your current Flexi-access investments. Use the
                      sliders to adjust your existing funds or click on the ‘add
                      another fund’ button to select other investment options.
                      <br />
                      <br />
                      Fund switches are carried out using units. The amount
                      shown is an estimate and the final value may be slightly
                      higher or lower due to changes in unit prices during
                      processing.
                    </Col>
                    <Col
                      xl={4}
                      lg={4}
                      md={4}
                      className="mf-fund-desc-button text-end"
                    >
                      <Buttons
                        name="Add another fund"
                        className="primary"
                        iconname="plus-white"
                        variantname="icon-sixteen-px"
                        handleClick={handleAddAnotherFund}
                        iconPosition="left"
                      ></Buttons>
                    </Col>
                  </Row>
                </div>
              </div>
              <div className="mf-eac-mobile-container d-block d-sm-none">
                <Card className="mf-mobile-card">
                  <Row>
                    <Col xl={3} lg={3} md={3} className="mf-mobile-dc-col">
                      <div className="mf-mobile-doughnut-chart">
                        <DoughnutChart
                          percentages={selectedFlexiFund.map(
                            (fund) => fund.fundPercentage, // eslint-disable-line
                          )}
                          colorPallete={selectedFlexiFund.map(
                            (fund) => fund.color, // eslint-disable-line
                          )}
                          showValue={false}
                        />
                      </div>
                    </Col>
                  </Row>
                  <Row className="mf-eac-mobile-total-value">
                    <div>[Flexi-access value]</div>
                  </Row>
                  <Row className="mf-eac-mobile-total-fund">
                    <div>
                      £
                      {props.directFromManage
                        ? addCommas(
                            Math.trunc(props.flexiAcessObject.currentValue), // eslint-disable-line
                          )
                        : addCommas(
                            Math.trunc(props.flexiAcessObject.newCurrentValue), // eslint-disable-line
                          )}
                      .
                      <span className="mf-eac-pot-value-small-digit">
                        {props.directFromManage
                          ? (props.flexiAcessObject.currentValue % 1)
                              .toFixed(2)
                              .substring(2)
                          : (props.flexiAcessObject.newCurrentValue % 1)
                              .toFixed(2)
                              .substring(2)}
                      </span>
                    </div>
                  </Row>
                  <hr />
                  <Row className="mf-eac-mobile-fund-dec mb-3">
                    <Col xl={8} lg={8} md={8}>
                      These are your current Flexi-access investments. Use the
                      sliders to adjust your existing funds or click on the ‘add
                      another fund’ button to select other investment options.
                    </Col>
                  </Row>
                  <Row className="mf-eac-add-button mb-3">
                    <Col xl={4} lg={4} md={4}>
                      <Buttons
                        name="Add another fund"
                        className="primary"
                        iconname="plus-white"
                        variantname="icon-sixteen-px"
                        handleClick={handleAddAnotherFund}
                        iconPosition="left"
                      ></Buttons>
                    </Col>
                  </Row>
                </Card>
              </div>
              <ExploreAndChangeInvestment
                category="Managed for me"
                data={selectedFlexiFund}
                totalFund={
                  props.directFromManage
                    ? props.flexiAcessObject.currentValue
                    : props.flexiAcessObject.newCurrentValue
                }
                onSliderChange={handleOnFlexiAccessSliderChange}
              />
              <ExploreAndChangeInvestment
                category="Self-managed"
                data={selectedFlexiFund}
                totalFund={
                  props.directFromManage
                    ? props.flexiAcessObject.currentValue
                    : props.flexiAcessObject.newCurrentValue
                }
                onSliderChange={handleOnFlexiAccessSliderChange}
              />
            </>
          ) : (
            <>
              <div className="cxfa-desc d-none d-xl-block d-lg-block d-md-block">
                Please select the funds for flexi-access. The next step will be
                to allocate the proportion in each fund.
              </div>
              <div className="mf-sub-card d-block d-sm-none">
                <Card className="mf-subheading">
                  Please select the funds for flexi-access. The next step will
                  be to allocate the proportion in each fund.
                </Card>
              </div>
              <FundSelect
                category="Managed for me"
                data={cmsFund}
                onCardSelect={handleCheckboxChange}
              />
              <FundSelect
                category="Self-managed"
                data={cmsFund}
                onCardSelect={handleCheckboxChange}
              />{" "}
            </>
          )}
        </div>
      </Card>
      {allocateFunds ? (
        <Card className="cxfa-footer-card">
          <div className="cxfa-footer-button">
            <div>
              <Buttons
                name="Cancel"
                className="secondary"
                variantname="icon-sixteen-px"
                handleClick={handleCancel}
              ></Buttons>
            </div>
            <div>
              <span className="funds-selected">
                Allocated:{" "}
                {selectedFlexiFund
                  .reduce(
                    (acc, fund) => acc + parseFloat(fund.fundPercentage),
                    0, // eslint-disable-line
                  )
                  .toFixed(2)}
                %{" "}
              </span>
              <Buttons
                name="Continue"
                className="primary"
                variantname="icon-sixteen-px"
                handleClick={handleFinalContinue}
                disabled={
                  selectedFlexiFund.reduce(
                    (acc, fund) => acc + parseFloat(fund.fundPercentage),
                    0, // eslint-disable-line
                  ) != 100 || !checkIsDirty
                }
              ></Buttons>
            </div>
          </div>
        </Card>
      ) : (
        <Card className="cxfa-footer-card">
          <div className="cxfa-footer-button">
            <div>
              <Buttons
                name="Cancel"
                className="secondary"
                variantname="icon-sixteen-px"
                handleClick={handleCancel}
              ></Buttons>
            </div>
            <div>
              <span className="funds-selected">
                {cmsFund?.filter((obj) => obj && obj.checked).length} funds
                selected
              </span>
              <Buttons
                name="Continue"
                className="primary"
                variantname="icon-sixteen-px"
                handleClick={handleContinue}
                disabled={
                  cmsFund?.filter((obj) => obj && obj.checked).length > 0
                    ? false
                    : true
                }
              ></Buttons>
            </div>
          </div>
        </Card>
      )}
      <ModalOverlay
        popup="chooseFlexiAccessIncome"
        show={chooseFlexiIncome}
        onHide={(e) => {
          if (e !== undefined) {
            e.preventDefault();
          }
          setFlexiAccessCurrentIncome(0);
          setFlexiAccessDuration("Monthly");
          setChooseFlexiIncome(false);
        }}
        modaltitle="Choose flexi-access income"
        flexiAccessCurrentIncome={flexiAccessCurrentIncome}
        flexiAccessDuration={flexiAccessDuration}
        handleFlexiAccessContinue={chooseFlexiAcessHandleContinue}
        handleProceedWithoutIncome={handleProceedWithoutIncome}
        currentValue={props?.flexiAcessObject.newCurrentValue}
        directFromManage={props.directFromManage}
        isCancel={false}
      />

      <ModalOverlay
        popup="flexiAccessIncomeIllustration"
        show={flexiAccessIncomeIllustration}
        onHide={(e) => {
          if (e !== undefined) {
            e.preventDefault();
          }
          setFlexiAccessIncomeIllustration(false);
        }}
        flexiAccessCurrentIncome={flexiAccessCurrentIncome}
        initialFlexiObject={flexiCurrentValue}
        updatedFlexiObject={updatedFlexiObject}
        modaltitle="Flexi-access income illustration"
        handleIllustrationContinue={handleIllustrationContinue}
        flexiAccessDuration={flexiAccessDuration}
        directFromManage={props.directFromManage}
      />

      <ModalOverlay
        popup="flexiAccessSummaryOfChanges"
        show={showFlexiSummaryPopup}
        onHide={(e) => {
          if (e !== undefined) {
            e.preventDefault();
          }
          setShowFlexiSummaryPopup(false);
        }}
        modaltitle="Summary of changes"
        finalFund={finalResult}
        handleContinue={handleSummaryContinue}
        fundSwitchError={fundSwitchError}
        errorShown={errorShown.current}
      />

      <ModalOverlay
        popup="flexiChangesConfirmed"
        show={changesConfirmPopup}
        onHide={(e) => {
          if (e !== undefined) {
            e.preventDefault();
          }
          setChangesConfirmPopup(false);
        }}
        modaltitle="Changes confirmed"
        handleContinue={handleConfirm}
      />
    </div>
  );
};

export default ManageFlexiAccess;
